import React, { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Upload, Pencil } from "lucide-react";
import api, { BACKEND_URL, formatApiErrorDetail } from "@/lib/api";
import { euro } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const emptyItem = { name: "", description: "", price: "", category_id: "", image_url: "", available: true, sort_order: 0 };

export default function MenuManager() {
  const [cats, setCats] = useState([]);
  const [items, setItems] = useState([]);
  const [newCat, setNewCat] = useState("");
  const [itemForm, setItemForm] = useState(emptyItem);
  const [editingItemId, setEditingItemId] = useState(null);
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    const [c, i] = await Promise.all([api.get("/categories"), api.get("/menu-items")]);
    setCats(c.data);
    setItems(i.data);
  };
  useEffect(() => { load(); }, []);

  const addCategory = async () => {
    if (!newCat.trim()) return;
    try {
      await api.post("/categories", { name: newCat, sort_order: cats.length });
      setNewCat("");
      await load();
      toast.success("Categoria aggiunta");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const delCategory = async (id) => {
    if (!window.confirm("Eliminare la categoria e i suoi piatti?")) return;
    await api.delete(`/categories/${id}`);
    await load();
  };

  const openAdd = (cat_id = "") => {
    setItemForm({ ...emptyItem, category_id: cat_id || cats[0]?.id || "" });
    setEditingItemId(null);
    setOpen(true);
  };

  const openEdit = (it) => {
    setItemForm({ ...it, price: String(it.price) });
    setEditingItemId(it.id);
    setOpen(true);
  };

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setItemForm((f) => ({ ...f, image_url: `${BACKEND_URL}${data.url}` }));
      toast.success("Immagine caricata");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const saveItem = async () => {
    const payload = {
      ...itemForm,
      price: parseFloat(itemForm.price) || 0,
      sort_order: Number(itemForm.sort_order) || 0,
    };
    try {
      if (editingItemId) await api.put(`/menu-items/${editingItemId}`, payload);
      else await api.post("/menu-items", payload);
      setOpen(false);
      await load();
      toast.success("Piatto salvato");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const delItem = async (id) => {
    if (!window.confirm("Eliminare il piatto?")) return;
    await api.delete(`/menu-items/${id}`);
    await load();
  };

  return (
    <div className="space-y-8" data-testid="menu-manager-page">
      <div className="flex justify-between items-end">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Menu</div>
          <h1 className="font-display text-4xl font-black tracking-tighter mt-1">Gestisci il menu</h1>
        </div>
        <Button className="pill-btn bg-primary text-primary-foreground hover:bg-[hsl(var(--accent))]" onClick={() => openAdd()} disabled={cats.length === 0} data-testid="add-item-btn">
          <Plus className="h-4 w-4 mr-2" /> Nuovo piatto
        </Button>
      </div>

      <div className="p-6 bg-card border border-border rounded-2xl space-y-4" data-testid="categories-card">
        <div className="text-sm font-medium">Categorie</div>
        <div className="flex flex-wrap gap-2">
          {cats.map((c) => (
            <div key={c.id} className="flex items-center gap-1 pl-3 pr-1 py-1 bg-secondary rounded-full text-sm" data-testid={`cat-chip-${c.id}`}>
              {c.name}
              <button onClick={() => delCategory(c.id)} className="hover:text-destructive p-1" data-testid={`cat-del-${c.id}`}>
                <Trash2 className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          <Input placeholder="Nuova categoria (es. Antipasti)" value={newCat} onChange={(e) => setNewCat(e.target.value)} data-testid="new-cat-input" />
          <Button onClick={addCategory} className="pill-btn" data-testid="add-cat-btn">
            <Plus className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="space-y-6">
        {cats.map((c) => (
          <div key={c.id} className="space-y-3">
            <div className="font-display text-xl font-bold">{c.name}</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {items.filter((it) => it.category_id === c.id).map((it) => (
                <div key={it.id} className="p-4 bg-card border border-border rounded-2xl flex gap-4" data-testid={`item-row-${it.id}`}>
                  <div className="w-20 h-20 rounded-lg overflow-hidden bg-secondary flex-shrink-0">
                    {it.image_url && <img src={it.image_url} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between gap-2">
                      <div className="font-medium truncate">{it.name}</div>
                      <div className="font-display font-bold">{euro(it.price)}</div>
                    </div>
                    <div className="text-xs text-muted-foreground line-clamp-2 mt-1">{it.description}</div>
                    <div className="mt-2 flex gap-2">
                      <Button size="sm" variant="ghost" onClick={() => openEdit(it)} data-testid={`edit-item-${it.id}`}>
                        <Pencil className="h-3 w-3" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => delItem(it.id)} data-testid={`del-item-${it.id}`}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
              {items.filter((it) => it.category_id === c.id).length === 0 && (
                <div className="col-span-full text-sm text-muted-foreground italic">Nessun piatto in questa categoria</div>
              )}
            </div>
          </div>
        ))}
        {cats.length === 0 && (
          <div className="p-12 text-center text-muted-foreground border border-dashed border-border rounded-2xl">
            Crea la prima categoria per iniziare.
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="item-dialog">
          <DialogHeader><DialogTitle>{editingItemId ? "Modifica piatto" : "Nuovo piatto"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nome</Label>
              <Input value={itemForm.name} onChange={(e) => setItemForm({ ...itemForm, name: e.target.value })} data-testid="item-name-input" />
            </div>
            <div>
              <Label>Descrizione</Label>
              <Textarea value={itemForm.description} onChange={(e) => setItemForm({ ...itemForm, description: e.target.value })} data-testid="item-desc-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Prezzo (€)</Label>
                <Input type="number" step="0.01" value={itemForm.price} onChange={(e) => setItemForm({ ...itemForm, price: e.target.value })} data-testid="item-price-input" />
              </div>
              <div>
                <Label>Categoria</Label>
                <Select value={itemForm.category_id} onValueChange={(v) => setItemForm({ ...itemForm, category_id: v })}>
                  <SelectTrigger data-testid="item-cat-select"><SelectValue placeholder="Categoria" /></SelectTrigger>
                  <SelectContent>{cats.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Immagine</Label>
              <div className="flex items-center gap-3">
                {itemForm.image_url && <img src={itemForm.image_url} alt="" className="w-16 h-16 object-cover rounded-lg" />}
                <input ref={fileRef} type="file" accept="image/*" onChange={(e) => upload(e.target.files?.[0])} className="hidden" data-testid="item-image-input" />
                <Button variant="outline" type="button" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="item-upload-btn">
                  <Upload className="h-4 w-4 mr-2" /> {uploading ? "..." : "Carica"}
                </Button>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <Label>Disponibile</Label>
              <Switch checked={itemForm.available} onCheckedChange={(v) => setItemForm({ ...itemForm, available: v })} data-testid="item-available-switch" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={saveItem} className="pill-btn bg-primary text-primary-foreground" data-testid="item-save-btn">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
